// Centralised SEO configuration.

// Production domain — swap here when launching elsewhere.
export const SITE_URL = "https://reformityinc.com";

// Branded OG share card — 1200x630, generated from the icon mark + wordmark.
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-share.jpg`;

export const SITE_NAME = "Reformity Inc.";

// Head-office coordinates for geo meta and JSON-LD. Must stay in sync with
// BUSINESS_ADDRESS below — a LocalBusiness whose geo and address disagree is
// worse than one with no geo at all. Approximate building centroid; replace
// with the exact pin from the Google Business Profile if it differs.
export const HQ_GEO = {
    lat: 43.7644,
    lon: -79.4164,
};

// Head office & mailing address — Reformity Inc. Feeds the LocalBusiness
// JSON-LD and the Privacy Policy contact block. Keep identical to the Google
// Business Profile and directory citations (NAP consistency).
export const BUSINESS_ADDRESS = {
    streetAddress: "200-5700 Yonge St",
    addressLocality: "North York",
    addressRegion: "ON",
    postalCode: "M2N 5M9",
    addressCountry: "CA",
};

// Display form of the head-office address, derived from BUSINESS_ADDRESS so the
// footer and the LocalBusiness JSON-LD can never drift apart. Two lines because
// that is how a NAP block reads in a footer.
export const BUSINESS_ADDRESS_LINES = [
    BUSINESS_ADDRESS.streetAddress,
    `${BUSINESS_ADDRESS.addressLocality}, ${BUSINESS_ADDRESS.addressRegion} ${BUSINESS_ADDRESS.postalCode}`,
];

// External profile URLs — used in LocalBusiness sameAs + trust badges
export const EXTERNAL_PROFILES = {
    bbb: "https://www.bbb.org/ca/on/brampton/profile/construction/reformity-inc-0107-1411380",
    homestars: "https://www.homestars.com/profile/2988577-reformity-inc",
    houzz: "https://www.houzz.com/professionals/general-contractors/reformity-inc-pfvwus-pf~1581828244",
    bark: "https://www.bark.com/en/ca/b/reformity-inc/RGGYG",
    google: "https://maps.google.com/?cid=17323474736504162501",
    facebook: "https://www.facebook.com/reformityinc",
    instagram: "https://www.instagram.com/reformityinc",
    youtube: "https://www.youtube.com/@ReformityInc.",
    linkedin: "https://www.linkedin.com/company/reformity-inc/",
};

// Aggregate rating across all 5 platforms — verified Nov 2025.
// Per-platform: Homestars 35 / 4.7, Bark 100 / 5.0, Houzz 1 / 5.0, BBB 7 / 5.0,
// Google 14 / 5.0. Weighted average = 4.93 → published as 4.9.
export const AGGREGATE_RATING = {
    total: 157,
    average: 4.9,
    bestRating: 5,
    worstRating: 1,
};

// Per-platform review metadata — used by Home TrustBar, Reviews page,
// per-testimonial source links. profileUrl is the destination for "View on X" links.
export const REVIEW_SOURCES = {
    Homestars: {
        name: "Homestars",
        profileUrl:
            "https://www.homestars.com/profile/2988577-reformity-inc/reviews",
        count: 35,
        rating: 4.7,
    },
    Bark: {
        name: "Bark",
        profileUrl: "https://www.bark.com/en/ca/b/reformity-inc/RGGYG",
        count: 100,
        rating: 5.0,
    },
    Houzz: {
        name: "Houzz",
        profileUrl:
            "https://www.houzz.com/professionals/general-contractors/reformity-inc-pfvwus-pf~1581828244",
        count: 1,
        rating: 5.0,
    },
    BBB: {
        name: "BBB",
        profileUrl:
            "https://www.bbb.org/ca/on/brampton/profile/construction/reformity-inc-0107-1411380",
        count: 7,
        rating: 5.0,
        accreditation: "A+",
    },
    Google: {
        name: "Google",
        profileUrl: "https://maps.google.com/?cid=17323474736504162501",
        count: 14,
        rating: 5.0,
    },
};

// Centralised page-level meta. Titles ≤ 60 chars, descriptions ≤ 160.
export const PAGE_META = {
    home: {
        title: "Reformity Inc. — Exterior & Interior Contractors GTA",
        description:
            "Premium fencing, interlocking, decking, concrete, landscaping & renovations across the Greater Toronto Area. Licensed & insured. Free quotes.",
    },
    services: {
        title: "Our Services — Reformity Inc. GTA Contractors",
        description:
            "Fencing, interlocking, decking, concrete, landscaping and interior renovations across the GTA. Licensed and insured. Get a free quote.",
    },
    fencing: {
        title: "Fence Installation Toronto & GTA — Cedar, Vinyl, Aluminum",
        description:
            "Cedar, vinyl, aluminum and chain-link fences built for GTA weather. Permit handling. 2-year workmanship warranty. Free quotes.",
    },
    interlocking: {
        title: "Interlocking Toronto & GTA — Driveways, Walkways, Patios",
        description:
            "Interlocking driveways, walkways and patios in Toronto, Brampton & the GTA. Compacted base, polymeric sand, lifetime base-prep warranty. Free quotes.",
    },
    decking: {
        title: "Custom Decks GTA — Composite & Pressure-Treated",
        description:
            "Composite (Trex/TimberTech) and pressure-treated decks engineered for 25+ Canadian seasons. Permit handling included. Free quotes.",
    },
    concrete: {
        title: "Concrete & Asphalt Driveways GTA — Reformity Inc.",
        description:
            "Driveways, walkways and structural slabs poured to spec. Stamped and decorative concrete. Asphalt paving and sealcoating across the GTA.",
    },
    landscaping: {
        title: "Landscaping GTA — Sod, Beds, Retaining Walls",
        description:
            "Sod, garden beds, retaining walls and full backyard transformations across the GTA. Drainage & grading included. Free quotes.",
    },
    renovations: {
        title: "Interior Renovations GTA — Kitchens, Baths, Basements",
        description:
            "Kitchen, bathroom, basement and epoxy floor renovations with fixed quotes. Licensed trades and permit handling. Serving the GTA.",
    },
    gallery: {
        title: "Project Gallery — Reformity Inc. GTA Contractors",
        description:
            "Recent fencing, interlocking, decking, concrete, landscaping and renovation projects across the Greater Toronto Area.",
    },
    reviews: {
        title: "Real Reviews — Reformity Inc. GTA Contractors",
        description:
            "Read real homeowner reviews of Reformity Inc. from Google, HomeStars, Bark, Houzz, and BBB — the GTA's trusted contracting team.",
    },
    reviewsGoogle: {
        title: "Reformity Inc Google Reviews — 5.0★ GTA Contractor",
        description:
            "Read all verified Google reviews of Reformity Inc., the GTA contracting team. 5.0★ across 14 reviews. Fencing, decking, interlocking, renovations.",
    },
    reviewsHomestars: {
        title: "Reformity Inc Homestars Reviews — 4.7★ Verified",
        description:
            "Read 35 verified Homestars reviews of Reformity Inc., the North York-based GTA contractor. 4.7★ average across fencing, decking, interlocking work.",
    },
    reviewsBark: {
        title: "Reformity Inc Bark Reviews — 5.0★ Certified Pro",
        description:
            "100 verified Bark reviews of Reformity Inc., a Bark Certified Pro contractor in the GTA. 5.0★ across fencing, decking, landscaping, and renovations.",
    },
    reviewsHouzz: {
        title: "Reformity Inc Houzz Reviews — 5.0★ Houzz Pro",
        description:
            "Read verified Houzz reviews of Reformity Inc., the GTA contracting team. Houzz Pro with 5.0★ rating for custom fencing and outdoor projects.",
    },
    reviewsBbb: {
        title: "Reformity Inc BBB Reviews — A+ Accredited GTA Contractor",
        description:
            "BBB Accredited A+ rating with 7 verified customer reviews of Reformity Inc., the trusted GTA contractor based in North York, Ontario.",
    },
    about: {
        title: "About Reformity Inc. — Founded by Shaheen Rashidi",
        description:
            "North York-based, GTA-serving contracting team founded in 2018 by Shaheen Rashidi. Licensed, insured, WSIB-covered. Modern tools, transparent quotes.",
    },
    contact: {
        title: "Get a Free Quote — Reformity Inc. Contractors",
        description:
            "Tell us about your project. We respond within 24 hours. Call 437-241-6287 or request a free quote online. Serving the GTA.",
    },
    serviceArea: {
        title: "Service Area — GTA & Golden Horseshoe Coverage",
        description:
            "Reformity Inc. serves Brampton, Mississauga, Toronto, Vaughan, Oakville, Burlington, Milton, Hamilton and 14 more GTA cities.",
    },
    blog: {
        title: "Blog — Reformity Inc. GTA Contractors",
        description:
            "Honest GTA contracting guides — fencing, decking, interlocking, landscaping and renovation pricing, bylaws, and how-to from Reformity Inc.",
    },
    // --- Three-track expansion ---
    splash: {
        title: "Reformity Inc. — Built for Every Property | GTA Contractors",
        description:
            "Residential, commercial & industrial exterior and general contracting across the Greater Toronto Area. Licensed, insured, WSIB. Choose your track.",
    },
    masonry: {
        title: "Masonry Contractor GTA — Brick, Parging & Underpinning",
        description:
            "Brickwork, tuckpointing, parging, foundation crack repair and underpinning across Brampton, Mississauga & the GTA. Free quotes from Reformity Inc.",
    },
    commercialHome: {
        title: "Commercial Contracting GTA — Property Exterior & Site Work",
        description:
            "Insured, scheduled commercial exterior & property contracting across the GTA. Fencing, asphalt, concrete, masonry & grounds for owners and property managers.",
    },
    commercialServices: {
        title: "Commercial Services — Reformity Inc. GTA Contractors",
        description:
            "Commercial fencing, parking lots & asphalt, concrete, hardscape, masonry restoration, grounds and exterior build-outs across the GTA. COIs & WSIB.",
    },
    industrialHome: {
        title: "Industrial Contracting GTA — Site & Facility Exterior Work",
        description:
            "Heavy-duty industrial site & facility contracting in the GTA — truck-rated paving, heavy concrete, security fencing, structural masonry. Compliant & insured.",
    },
    industrialServices: {
        title: "Industrial Services — Reformity Inc. GTA Contractors",
        description:
            "Heavy-duty asphalt, industrial concrete, security fencing, structural masonry, retaining walls, bollards and drainage for GTA industrial sites.",
    },
    commercialContact: {
        title: "Commercial Quote — Reformity Inc. GTA Property Contracting",
        description:
            "Request a scoped commercial exterior & property contracting quote. COIs & WSIB provided. Single-site or portfolio. We respond within one business day.",
    },
    industrialContact: {
        title: "Industrial Quote — Reformity Inc. GTA Site & Facility Work",
        description:
            "Request an engineered industrial site & facility quote. Compliance documentation provided, scheduled around your operations. One business-day response.",
    },
    commercialAbout: {
        title: "About — Reformity Inc. Commercial Contracting GTA",
        description:
            "North York-based, GTA-serving commercial contractor. Licensed, insured, WSIB-compliant, COI-ready. One accountable vendor for property owners and managers.",
    },
    industrialAbout: {
        title: "About — Reformity Inc. Industrial Contracting GTA",
        description:
            "North York-based, GTA-serving industrial contractor. Licensed, insured, WSIB & health-&-safety compliant. Heavy-duty site & facility work, documented.",
    },
    commercialServiceArea: {
        title: "Commercial Service Area — GTA & Golden Horseshoe",
        description:
            "Reformity serves commercial properties across Brampton, Mississauga, Toronto, Vaughan and the wider GTA & Golden Horseshoe. Single-site or portfolio.",
    },
    industrialServiceArea: {
        title: "Industrial Service Area — GTA & Golden Horseshoe",
        description:
            "Reformity serves industrial sites and facilities across the GTA & Golden Horseshoe — warehouses, plants and yards in Brampton, Mississauga, Toronto and beyond.",
    },
    commercialGallery: {
        title: "Commercial Work — Reformity Inc. GTA Contractors",
        description:
            "Examples of commercial exterior and property work across the GTA — fencing, parking lots, concrete, hardscape, masonry restoration and grounds.",
    },
    industrialGallery: {
        title: "Industrial Work — Reformity Inc. GTA Contractors",
        description:
            "Examples of heavy-duty industrial site and facility work across the GTA — paving, concrete, security fencing, structural masonry, bollards and drainage.",
    },
    privacy: {
        title: "Privacy Policy — Reformity Inc.",
        description:
            "How Reformity Inc. collects, uses, stores and shares personal information from quote requests, our chat assistant and site analytics. PIPEDA-aligned.",
    },
    admin: {
        title: "Admin — Reformity Inc.",
        description: "Reformity Inc. admin area.",
    },
    notFound: {
        title: "Page Not Found — Reformity Inc.",
        description: "The page you were looking for couldn't be found.",
    },
};
